import React from 'react';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import messageUtils from '~/shared/src/utilities/messageUtils';

import {PanelClass} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

/**
 *
 * @param str
 */
export const br2nl = ( str : string ) : string => {
    return String( str ).replace( /<br *\/*>/g, '\r\n' );
}

/**
 *
 * @param str
 */
export const nl2br = ( str : string ) : string => {
    return String( str ).replace( /\r?\n/g, '<br/>' );
}

/**
 *
 * @param itemType
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getItemKeyDisplayName = function( itemType : string | number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const defaultName = __( 'YN.base.common.item.key' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( itemType ) {
        retVal = metaModel[ schema ]?.getItemType( itemType )?.keyLabel ?? '';
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param itemType
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getItemNameDisplayName = function( itemType : string | number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const defaultName = __( 'YN.base.common.item.name' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( itemType ) {
        retVal = metaModel[ schema ]?.getItemType( itemType )?.nameLabel ?? '';
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param itemType
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getItemTypeDisplayName = function( itemTypeId : string | number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const itemType = metaModel[ schema ]?.getItemType( itemTypeId );

    let retVal = '';

    if ( itemType ) {
        if ( rawMode ) {
            retVal = itemType.name;
        }
        else {
            retVal = itemType.label;
            if ( showAdditionalRawNames && retVal !== itemType.name ) {
                retVal += ` (${itemType.name})`;
            }
        }
    }

    return retVal;
}

/**
 *
 * @param propertyTypeId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getPropertyTypeDisplayName = function( propertyTypeId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const propertyType = metaModel[ schema ]?.getPropertyType( propertyTypeId );

    let retVal = '';

    if ( propertyType ) {
        if ( rawMode ) {
            retVal = propertyType.name;
        }
        else {
            retVal = propertyType.label;
            if ( showAdditionalRawNames && retVal !== propertyType.name ) {
                retVal += ` (${propertyType.name})`;
            }
        }
    }

    return retVal;
}

/**
 *
 * @param propertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getPropertySetDisplayName = function( propertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const propertySet = metaModel[ schema ]?.getPropertySet( propertySetId );

    let retVal = '';

    if ( propertySet ) {
        if ( rawMode ) {
            retVal = propertySet.name;
        }
        else {
            retVal = propertySet.label;
            if ( showAdditionalRawNames && retVal !== propertySet.name ) {
                retVal += ` (${propertySet.name})`;
            }
        }
    }

    return retVal;
}

/**
 *
 * @param propertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getPropertyKeyDisplayName = function( propertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const propertySet = metaModel[ schema ]?.getPropertySet( propertySetId ),
          defaultName = __( 'YN.base.common.property.key' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( propertySet ) {
        retVal = propertySet.keyLabel;
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param propertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getPropertyValueDisplayName = function( propertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const propertySet = metaModel[ schema ]?.getPropertySet( propertySetId ),
          defaultName = __( 'YN.base.common.property.value' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( propertySet ) {
        retVal = propertySet.valueLabel;
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param referenceSetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getReferenceTypeDisplayName = function( referenceSetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const referenceSet = metaModel[ schema ]?.getReferenceSet( referenceSetId );

    let retVal = '';

    if ( referenceSet ) {
        if ( rawMode ) {
            retVal = referenceSet.referenceTypeName;
        }
        else {
            retVal = referenceSet.referenceTypeLabel;
            retVal += showAdditionalRawNames && retVal !== referenceSet.referenceTypeName
                ? ` (${referenceSet.referenceTypeName})`
                : '';
        }
    }

    return retVal;
}

/**
 *
 * @param referenceSetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getReferenceSetDisplayName = function( referenceSetId : number, schema : Schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const referenceSet = metaModel[ schema ]?.getReferenceSet( referenceSetId );

    let retVal = '';

    if ( referenceSet ) {
        retVal = `${getItemTypeDisplayName( referenceSet.srcItemTypeId, schema, rawMode, showAdditionalRawNames )} → ${getReferenceTypeDisplayName( referenceSetId, schema, rawMode, showAdditionalRawNames )} → ${getItemTypeDisplayName( referenceSet.destItemTypeId, schema, rawMode, showAdditionalRawNames )}`;
    }

    return retVal;
}

/**
 *
 * @param referenceSetId
 * @param itemTypeName
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 * @param direction
 */
export const getReferenceSetDisplayNameFromItemType = function( referenceSetId : number, itemTypeName : string, schema : Schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false, direction? : 'src' | 'dest' ) : string {
    const referenceSet = metaModel[ schema ]?.getReferenceSet( referenceSetId );

    let retVal = '';

    if ( referenceSet ) {
        if ( !direction ) {
            direction = referenceSet.srcItemType.name === itemTypeName
                ? 'dest'
                : 'src';
        }

        const targetItemTypeId = direction === 'dest'
            ? referenceSet.destItemTypeId
            : referenceSet.srcItemTypeId;

        // \u200d - non-width space added for sorting purposes (desc before src references in ascending order)
        retVal = `${direction === 'dest' ? '\u200d→' : '←'} ${getItemTypeDisplayName( targetItemTypeId, schema, rawMode, showAdditionalRawNames )} (${getReferenceTypeDisplayName( referenceSetId, schema, rawMode, showAdditionalRawNames )})`;
    }

    return retVal;
}

/**
 *
 * @param referencePropertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getReferencePropertySetDisplayName = function( referencePropertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const referencePropertySet = metaModel[ schema ]?.getReferencePropertySet( referencePropertySetId );

    let retVal = '';

    if ( referencePropertySet ) {
        if ( rawMode ) {
            retVal = referencePropertySet.name;
        }
        else {
            retVal = referencePropertySet.label;
            if ( showAdditionalRawNames && retVal !== referencePropertySet.name ) {
                retVal += ` (${referencePropertySet.name})`;
            }
        }
    }

    return retVal;
}

/**
 *
 * @param referencePropertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getReferencePropertyKeyDisplayName = function( referencePropertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const referencePropertySet = metaModel[ schema ]?.getReferencePropertySet( referencePropertySetId ),
          defaultName          = __( 'YN.base.common.property.key' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( referencePropertySet ) {
        retVal = referencePropertySet.keyLabel;
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param referencePropertySetId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getReferencePropertyValueDisplayName = function( referencePropertySetId : number, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    const referencePropertySet = metaModel[ schema ]?.getReferencePropertySet( referencePropertySetId ),
          defaultName          = __( 'YN.base.common.property.value' );

    let retVal = '';

    if ( rawMode ) {
        retVal = defaultName;
    }
    else if ( referencePropertySet ) {
        retVal = referencePropertySet.valueLabel;
        if ( showAdditionalRawNames && retVal !== defaultName ) {
            retVal += ` (${defaultName})`;
        }
    }
    else {
        retVal = defaultName;
    }

    return retVal;
}

/**
 *
 * @param valueSetId
 * @param value
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getValueDisplayName = function( valueSetId : number, value : number | string, schema : Schema = global.schema, rawMode = false, showAdditionalRawNames = false ) : string {
    const mmValue = metaModel[ schema ]?.getValueSetValue( valueSetId, value );

    let retVal = '';

    if ( mmValue ) {
        if ( rawMode ) {
            retVal = mmValue.value;
        }
        else {
            retVal = mmValue.label;
            if ( showAdditionalRawNames && retVal !== mmValue.value ) {
                retVal += ` (${mmValue.value})`;
            }
        }
    }

    return retVal;
}

/**
 *
 * @param statusId
 * @param schema
 * @param [rawMode=false]
 * @param [showAdditionalRawNames=false]
 */
export const getStatusDisplayName = function( statusId : number | string, schema : Schema = global.schema, rawMode : boolean = false, showAdditionalRawNames : boolean = false ) : string {
    let retVal = '';

    if ( statusId ) {
        if ( rawMode ) {
            retVal = statusId + '';
        }
        else {
            const status = metaModel[ schema ]?.getStatus( statusId );

            if ( status ) {
                retVal = status.label;

                if ( showAdditionalRawNames ) {
                    retVal += ` (${status.name})`;
                }
            }
            else {
                retVal = String( statusId );
            }
        }
    }

    return retVal;
};

/**
 *
 * @param statusId
 * @param schema
 */
export const getStatusColorStyleString = function( statusId : number | string, schema : Schema = global.schema ) : string {
    let retVal = '';

    if ( statusId ) {
        retVal = `style="color: #${metaModel[ schema ]?.getStatus( statusId )?.color};"`;
    }

    return retVal;
};

/**
 *
 * @param statusId
 * @param schema
 */
export const getStatusColorStyleObject = function( statusId : number | string, schema : Schema = global.schema ) : React.CSSProperties {
    let retVal = {};

    if ( statusId ) {
        const status = metaModel[ schema ]?.getStatus( statusId );

        if ( status?.name !== 'active' ) {
            retVal = { color: `#${status?.color}` };
        }
    }

    return retVal;
};

/**
 * decodes and returns all htmlentities to normal chars in the currently used charset
 * @param str text to decode
 */
export const htmlEntityDecode = function( str : string ) : string {
    const ta = document.createElement( 'textarea' );

    ta.innerHTML = str.replace( /</g, '&lt;' ).replace( />/g, '&gt;' );

    return ta.value;
};

/**
 * encodes ", ', < and > to html entities
 *
 * @param str text to decode
 * @param encodeAmps true to encode also the & characters (needed for template usage!)
 * @param encodeBackslashes true to encode also backslashes (needed for template usage!)
 */
export const htmlQuoteEncode = function( str : string, encodeAmps : boolean = false, encodeBackslashes : boolean = false ) : string {
    let retVal = str,
        cpMcEditTags : string[] | null;

    if ( str === undefined ) {
        retVal = '';
    }
    // TODO: remove this if all usages are typescript save
    else if ( typeof str === 'string' ) {
        if ( encodeAmps ) {
            retVal = retVal.replace( /&/g, '&amp;' );
        }
        if ( encodeBackslashes ) {
            retVal = retVal.replace( /\\/g, '&bsol;' );
        }

        // replace special tags
        cpMcEditTags = retVal.match( /<span[^>]*class='cp-messagecatalogue-edit'[^>]*>[^<]*<\/span>/g );
        if ( cpMcEditTags?.length ) {
            retVal = retVal.replace( /<span[^>]*class='cp-messagecatalogue-edit'[^>]*>[^<]*<\/span>/g, '-::cp-messagecatalogue-edit::-' );
        }

        // replace escaped chars with placeholders
        retVal = retVal.replace( /\\"/g, '-::-quot-::-' ).replace( /\\'/g, '-::-apos-::-' ).replace( /\\</g, '-::-lt-::-' ).replace( /\\>/g, '-::-gt-::-' );
        // replace non escaped chars with html entity
        retVal = retVal.replace( /"/g, '&quot;' ).replace( /'/g, '&apos;' ).replace( /</g, '&lt;' ).replace( />/g, '&gt;' );
        // replace placeholders with non escaped char
        retVal = retVal.replace( /-::-quot-::-/g, '"' ).replace( /-::-apos-::-/g, '\'' ).replace( /-::-lt-::-/g, '<' ).replace( /-::-gt-::-/g, '>' );

        // restore special tags
        if ( cpMcEditTags?.length ) {
            retVal = retVal.replace( /-::cp-messagecatalogue-edit::-/g, function() {
                return cpMcEditTags?.shift() ?? '';
            } );
        }
    }

    return retVal;
};

/**
 *
 * @param text
 */
const prepareTextForRegExp = function( text : string ) : string {
    let retVal = text.trim();

    // contain no wildcard character -> pre- and append one
    if ( !retVal.match( /(\_|\%|\_|\*)/i ) ) {
        retVal = '*' + retVal + '*';
    }

    // ** -> *
    retVal = retVal.replace( /\*\*/, '*' );

    // escape single backslash if not used as escape char
    retVal = retVal.replace( /[\\](?!([+_\?*%]))/g, '\\\\' );

    // masking special chars ( + . [ ] ( ) { } )
    retVal = retVal.replace( /[+.\[\]\(\)\{\}]/g, '\\$&' );

    return retVal;
};

const buildFilterRegExpCache : { [ key : string ] : RegExp } = {};

/**
 *
 * @param text
 */
const buildFilterRegExp = function( text : string ) : RegExp {
    if ( !buildFilterRegExpCache[ text ] ) {
        let retVal = text;

        // replace escaped backslashes (double backslash) and temporarily replace with placeholder
        retVal = retVal.replace( /\\\\/g, '##@##' );

        // Replace wildcards with appropriate regex equivalents (only if not preceeded by backslash)
        // NOTE: query is reversed in order to do a negative lookahead (lookbehind not directly supported in JS)
        retVal = retVal.split( '' ).reverse().join( '' );
        retVal = retVal.replace( /[_\?](?!([\\]))/g, '().()' );
        //retVal = retVal.replace( /[*%](?!([\\]))/g, '()?*.()' );
        retVal = retVal.replace( /[*%](?!([\\]))/g, '' );
        retVal = retVal.split( '' ).reverse().join( '' );

        // replace temp placeholder with with backslashes again
        retVal = retVal.replace( /##@##/g, '\\\\' );

        // remove escaped wildcards for check
        /*const noEscapedWildcardsQuery = retVal.replace( /\\\*|\\\%|\\\./g, '' );

        // if contains no wildcards, then append wildcard
        if ( !/[*.]/g.test( noEscapedWildcardsQuery ) ) {
            retVal += ')(?:.*)(';
        }*/

        // build regular expression for finding filter search hits
        // starts exactly with filter or not
        if ( text.indexOf( '*' ) !== 0 ) {
            retVal = '^(' + retVal;
        }
        else {
            retVal = '(' + retVal;
        }
        // ends exactly with filter or not
        if ( text.lastIndexOf( '*' ) !== text.length - 1 ) {
            retVal = retVal + ')$';
        }
        else {
            retVal = retVal + ')';
        }

        buildFilterRegExpCache[ text ] = new RegExp( retVal, 'igm' );
    }

    return buildFilterRegExpCache[ text ];
};

export const markFilterSearchesInText = function( text : string, filter : string, isTextHtml : boolean = false ) : string {
    const tagContents : { [ tagIdentifier : string ] : string } = {};

    let retVal = '',
        count : number;

    text = String( text );
    filter = String( filter );

    if ( text && filter ) {
        // split filter in parts by || or &&
        const filterParts                                                                          = filter.split( /\|\||&&/ ),
              filterPartPositions : { [ position : number ] : { start : number; stop : number; } } = {};

        // replace all tags with placeholders
        if ( isTextHtml ) {
            let matches : string[] | null;

            while ( ( matches = text.match( /(\<[^<>]*\>)/im ) ) ) {
                const tagIdentifier = `::ταγ::${Object.keys( tagContents ).length}::`;

                tagContents[ tagIdentifier ] = matches[ 0 ];
                text = text.replace( /(\<[^<>]*\>)/im, tagIdentifier );
            }
        }

        for ( let filterPartsIndex = 0; filterPartsIndex < filterParts.length; filterPartsIndex++ ) {
            let filterPart = filterParts[ filterPartsIndex ];

            // skip conditions in filter
            if ( filterPart !== '' && !new RegExp( '^(is )?(not )?(empty|null)$', 'i' ).test( filterPart )
                && !new RegExp( '^((<|<=|!=|<>|>|>=|=) ).*$', 'i' ).test( filterPart ) ) {
                // prepare for usage in regular expression
                filterPart = prepareTextForRegExp( filterParts[ filterPartsIndex ] );

                // build regular expression for filter search
                const filterPartRegExp = buildFilterRegExp( filterPart );

                // find filter search hit
                const filterTextParts = text.split( filterPartRegExp );

                // get filter search hit positions
                if ( filterTextParts.length > 1 ) {
                    let filterPartText = '';

                    for ( count = 0; count < filterTextParts.length; count++ ) {
                        if ( filterTextParts[ count ] !== '' ) {
                            if ( count % 2 === 0 ) {
                                filterPartText += filterTextParts[ count ];
                            }
                            else {
                                if ( !filterPartPositions[ filterPartText.length ] ) {
                                    filterPartPositions[ filterPartText.length ] = {
                                        start: 0,
                                        stop : 0
                                    }
                                }
                                filterPartPositions[ filterPartText.length ].start++;

                                filterPartText += filterTextParts[ count ];

                                if ( !filterPartPositions[ filterPartText.length ] ) {
                                    filterPartPositions[ filterPartText.length ] = {
                                        start: 0,
                                        stop : 0
                                    }
                                }
                                filterPartPositions[ filterPartText.length ].stop++;
                            }
                        }
                    }
                }
            }
        }

        retVal = text;

        // order by number desc
        const orderedPositions = Object.keys( filterPartPositions ).sort( ( v1, v2 ) => {
            v1 = Number( v1 );
            v2 = Number( v2 );
            if ( v1 > v2 ) {
                return -1;
            }
            else if ( v2 > v1 ) {
                return 1;
            }
            else {
                return 0;
            }
        } );

        // add start and stop tags at positions
        for ( const position of orderedPositions ) {
            retVal = retVal.substring( 0, position ) + '</span>'.repeat( filterPartPositions[ position ].stop ) + retVal.substring( position );
            retVal = retVal.substring( 0, position ) + '<span class="yn-filter-resultpart">'.repeat( filterPartPositions[ position ].start ) + retVal.substring( position );
        }
    }

    if ( !retVal ) {
        retVal = text;
    }

    // replace all placeholders with tags
    if ( isTextHtml ) {
        while ( retVal.match( /::ταγ::[0-9]+::/gim ) ) {
            retVal = retVal.replace( /::ταγ::[0-9]+::/gim, ( tagIdentifier ) => tagContents[ tagIdentifier ] );
        }
    }

    return retVal;
};

/**
 * Wraps the given keyValuePairs into an table for usage on tooltips.
 *
 * @param keyValuePairs            An object containing key value pairs.
 * @param noMaxWidth
 * @param withTableHeader
 * @param noFixFirstColumn
 * @param noBoldKeys
 */
export const getTooltipTable = function( keyValuePairs : Record<string, string> | string[][], noMaxWidth : boolean = false, withTableHeader : boolean = false, noFixFirstColumn : boolean = false, noBoldKeys : boolean = true ) : string {
    let retVal,
        key,
        value,
        rowCount = 0,
        colCount = 0,
        i, k;
    const altRowCls     = 'yn-datatable-odd',
          headerCellCls = 'x-gridcolumn-title-wrap-el',
          cellCls       = 'x-gridcell-body-el',
          keyConvert    = ( text : string ) => {
              return ( text + '' ).replace( /"/g, '\'' );
          },
          valueConvert  = ( text : string ) => {
              return ( text + '' ).replace( /"/g, '\'' );
          };

    retVal = `<table class="info-table ${noBoldKeys
        ? 'no-bold-keys'
        : ''}" ${noMaxWidth
        ? 'style="maxWidth: none !important;"'
        : ''}>`;
    retVal += `<colgroup><col ${noFixFirstColumn
        ? ''
        : 'width="120"'}><col></colgroup>`;
    retVal += '<tbody>';

    // table head if
    if ( withTableHeader ) {
        if ( Array.isArray( keyValuePairs ) ) {
            retVal += '<tr>';
            for ( k = 0; k < keyValuePairs[ 0 ].length; k++ ) {
                key = keyConvert( keyValuePairs[ 0 ][ k ] );
                retVal += `<td class="${headerCellCls}">${key}</td>`;
            }
            retVal += '</tr>';
        }
        else {
            for ( key in keyValuePairs ) {
                key = keyConvert( key );
                value = valueConvert( keyValuePairs[ key ] );

                retVal += '<tr>';
                retVal += `<td class="${headerCellCls}">${key}</td>`;
                retVal += `<td class="${headerCellCls}">${value}</td>`;
                retVal += '</tr>';

                delete keyValuePairs[ key ];

                break;
            }
        }
    }

    // table body
    if ( Array.isArray( keyValuePairs ) ) {
        for ( i = 1; i < keyValuePairs.length; i++ ) {
            if ( i === 1 ) {
                colCount = keyValuePairs[ i ].length;
            }
            if ( rowCount % 2 === 0 ) {
                retVal += '<tr>';
            }
            else {
                retVal += `<tr class="${altRowCls}">`;
            }

            for ( k = 0; k < keyValuePairs[ i ].length; k++ ) {
                value = valueConvert( keyValuePairs[ i ][ k ] );

                // last col spans to fit col count
                if ( k === keyValuePairs[ i ].length - 1 && k < colCount - 1 ) {
                    retVal += `<td class="${cellCls}" colspan="${colCount - k}">${value}</td>`;
                }
                else {
                    retVal += `<td class="${cellCls}">${value}</td>`;
                }
            }

            retVal += '</tr>';

            rowCount++;
        }
    }
    else {
        for ( key in keyValuePairs ) {
            key = keyConvert( key );
            value = valueConvert( keyValuePairs[ key ] );

            if ( rowCount % 2 === 0 ) {
                retVal += '<tr>';
            }
            else {
                retVal += `<tr class="${altRowCls}">`;
            }

            retVal += `<td class="${cellCls}">${key}</td>`;
            retVal += `<td class="${cellCls}">${value}</td>`;

            retVal += '</tr>';

            rowCount++;
        }
    }

    retVal += '<tbody>';
    retVal += '</table>';

    return retVal;
};

export const getFinalColor = ( color : string, backgroundColor : string ) : string => {
    if ( !color.match( /^#/ ) ) {
        color = `#${color}`;
    }
    if ( color.length === 4 || color.length === 7 ) {
        return color;
    }

    if ( !backgroundColor.match( /^#/ ) ) {
        backgroundColor = `#${backgroundColor}`;
    }

    const rgb = color.length === 4 || color.length === 5 ? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})?$/i.exec( color ) : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec( color ),
          r   = parseInt( rgb?.[ 1 ] ?? 'ff', 16 ),
          g   = parseInt( rgb?.[ 2 ] ?? 'ff', 16 ),
          b   = parseInt( rgb?.[ 3 ] ?? 'ff', 16 ),
          op  = parseInt( rgb?.[ 4 ] ?? 'ff', 16 ) / 255;

    if ( op === 255 ) {
        return color;
    }

    const rgb2   = backgroundColor.length === 4 || backgroundColor.length === 5 ? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})?$/i.exec( backgroundColor ) : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec( backgroundColor ),
          r2     = parseInt( rgb2?.[ 1 ] ?? 'ff', 16 ),
          g2     = parseInt( rgb2?.[ 2 ] ?? 'ff', 16 ),
          b2     = parseInt( rgb2?.[ 3 ] ?? 'ff', 16 ),
          finalR = Math.round( r2 * ( 1.0 - op ) + r * op ),
          finalG = Math.round( g2 * ( 1.0 - op ) + g * op ),
          finalB = Math.round( b2 * ( 1.0 - op ) + b * op );

    return `#${finalR < 10 ? '0' : ''}${finalR.toString( 16 )}${finalG < 10 ? '0' : ''}${finalG.toString( 16 )}${finalB < 10 ? '0' : ''}${finalB.toString( 16 )}`;
}

export const getTextColorForBackgroundColor = ( backgroundColor : string, backgroundColor2? : string ) : string => {
    const colorScheme = matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';

    if ( !backgroundColor.match( /^#/ ) ) {
        backgroundColor = `#${backgroundColor}`;
    }

    if ( !backgroundColor2 ) {
        backgroundColor2 = colorScheme === 'light' ? '#ffffff' : '#121212';
    }
    if ( !backgroundColor2.match( /^#/ ) ) {
        backgroundColor2 = `#${backgroundColor2}`;
    }

    const color      = getFinalColor( backgroundColor, backgroundColor2 ),
          rgb        = color.length === 4 || color.length === 5 ? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i.exec( color ) : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( color ),
          r          = parseInt( rgb?.[ 1 ] ?? 'ff', 16 ),
          g          = parseInt( rgb?.[ 2 ] ?? 'ff', 16 ),
          b          = parseInt( rgb?.[ 3 ] ?? 'ff', 16 ),
          brightness = rgb
              ? Math.round( (
                  ( r * 299 ) +
                  ( g * 587 ) +
                  ( b * 114 )
              ) / 1000 )
              : 255;

    return ( brightness > 125 ) ? '#495057' : '#f8f9fa';
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const getTabTitleAndIcon = ( ViewComponent : Partial<PanelClass>, dataContext : string, schema : Schema, rootItemId : number, labelKey? : string ) : {
    title : string | undefined,
    icon : string | undefined
} => {
    const
        item                     = global.data[ dataContext ]?.items?.[ rootItemId ],
        labelTemplateResolver    = item && labelKey ? messageUtils.getMessageTemplateStringResolver( item ) : null,
        itemTypeDisplayAttribute = item ? item.itemType.displayAttribute : undefined,
        title                    = labelTemplateResolver
            ? labelTemplateResolver( __( labelKey! ) )
            : item && itemTypeDisplayAttribute
                ? `${item[ itemTypeDisplayAttribute ]} (${item.id > 0 ? item.id : 'NEW'})`
                : ViewComponent.panelTabTitle ?? '',
        itemTypeIcon             = item
            ? item.itemType.icon
            : '',
        icon                     = itemTypeIcon
            ? `yn-tab-image-icon icon-itemtype-detail ${itemTypeIcon}`
            : ViewComponent.panelTabIcon ?? '';

    return { title, icon };
}

type Attributes = {
    [ attribute : string ] : any;
};

export const resolveLigatureIcon = ( className : string, attributes : Attributes = {} ) : string | ( () => React.ReactNode ) => {
    let retVal : string | ( () => React.ReactNode ) = className;

    if ( retVal.match( /::[a-zA-Z0-9-_]*::/ ) ) {
        retVal = () => <span
            className={className.replaceAll( /::[a-zA-Z0-9-_]*::/gim, '' )}
            {...attributes}
        >
                {className.match( /::([a-zA-Z0-9-_]*)::/ )?.[ 1 ]}
            </span>;
    }
    else if ( Object.keys( attributes ).length ) {
        retVal = () => <span className={className}{...attributes}/>;
    }

    return retVal;
}

export const getIconSpan = ( className : string, attributes : Attributes = {} ) : React.ReactNode => {
    let retVal : React.ReactNode;

    if ( className.match( /::[a-zA-Z0-9-_]*::/ ) ) {
        retVal = <span
            className={className.replaceAll( /::[a-zA-Z0-9-_]*::/gim, '' )}
            {...attributes}
        >
            {className.match( /::([a-zA-Z0-9-_]*)::/ )?.[ 1 ]}
        </span>;
    }
    else {
        retVal = <span className={className}{...attributes}/>;
    }

    return retVal;
}

export const getIconStringSpan = ( className : string, attributes : Attributes = {} ) : string => {
    const attributesString = Object.keys( attributes ).map( ( attribute ) => ` ${attribute}="${attributes[ attribute ]}"` ).join( '' );

    let retVal : string;

    if ( className.match( /::[a-zA-Z0-9-_]*::/ ) ) {
        retVal = `<span class="${className.replaceAll( /::[a-zA-Z0-9-_]*::/gim, '' )}"${attributesString}>${className.match( /::([a-zA-Z0-9-_]*)::/ )?.[ 1 ]}</span>`;
    }
    else {
        retVal = `<span class="${className}"${attributesString}></span>`;
    }

    return retVal;
}

export type ImageDimensions = {
    w : string;
    h : string;
};
export const getIconFromImage = ( imageData : string, imageDimensions : ImageDimensions = { w: '16px', h: '1rem' }, className : string = '', attributes = {} ) => {
    const imageDataParts                             = imageData.split( '::rn::' ),
          imgStyle : React.CSSProperties             = {
              height       : imageDimensions.h,
              width        : imageDimensions.w,
              verticalAlign: 'bottom',
              maxWidth     : 'none'
          },
          imgSrcSets : { [ key : string ] : string } = {};

    let retVal : React.ReactNode,
        imgClassName = '';

    for ( const data of imageDataParts ) {
        let matches = data.match( /^imageData\(\s*"key"\s*:\s*"(?<key>[^"]+)"\s*,\s*"value"\s*:\s*"(?<value>[^"]+)"/ );

        if ( matches?.groups ) {
            if ( !imgSrcSets[ '1x' ] ) {
                imgSrcSets[ '1x' ] = matches.groups.value;
            }
            imgSrcSets[ matches.groups.key ] = matches.groups.value;
        }

        matches = data.match( /^cssClass\(\s*(?<class>[^)]*)\s*\)/ );

        if ( matches?.groups ) {
            imgClassName = matches.groups.class;
        }
    }

    // only imageData given (not wrapped and labeled)
    if ( !Object.keys( imgSrcSets ).length && imgClassName === '' && imageDataParts.length === 1 ) {
        imgSrcSets[ '1x' ] = imageDataParts[ 0 ];
    }

    if ( Object.keys( imgSrcSets ).length ) {
        retVal = <span
            className={`${className} ${imgClassName}`}
            {...attributes}
        >
            <img
                alt="icon"
                src={imgSrcSets[ '1x' ]}
                srcSet={Object.keys( imgSrcSets ).map( k => `${imgSrcSets[ k ]} ${k}` ).join( ',' )}
                style={imgStyle}
            />
            </span>;
    }
    else {
        retVal = <span
            className={`${className} ${imgClassName}`}
            {...attributes}
        />;
    }

    return retVal;
}
